import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { X, MessageSquareMore } from "lucide-react";

import { CarSVG } from "./CarSVG";
import CommentPartModal from "./CommentPartModal";
import { usePageConfig } from "../hooks/usePageConfig";
import { buildMediaUrl } from "../utils/mediaUrl";
import ImageLightbox from "./ImageLightbox";

const SingleDamagedPartModal = ({
  isOpen,
  onClose,
  assessmentData,
  initialFilter = "all",
  initialSeverityFilter = "",
  onRefreshAssessment,
  partDetail,
  partsDamageMap,
  setPartName,
  clickedPart,
  setClickedPart,
  initialActiveView,
}) => {
  const { clientConfig } = usePageConfig();
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCommentModalOpen, setIsCommentModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxCurrentIndex, setLightboxCurrentIndex] = useState(0);
  const [activeView, setActiveView] = useState(initialActiveView || 0);
  const [displayedImages, setDisplayedImages] = useState([]);
  const [showImageEmptyState, setShowImageEmptyState] = useState(false);
  const [currentPartDetail, setCurrentPartDetail] = useState(partDetail);
  const activeViewRef = useRef(initialActiveView || 0);
  const hasSeededInitialSelectionRef = useRef(false);
  const hasModalSelectionRef = useRef(false);

  const [selectedPart, setSelectedPart] = useState(null);

  const getInitialAction = (part) => {
    if (!part) return "No Damage";

    // Check various possible fields for action/status
    if (part.Action) return part.Action;
    if (part.action) return part.action;
    if (part.suggested_action) return part.suggested_action;
    if (part.status) return part.status;
    if (part.repair_status) return part.repair_status;

    // Check in damage_images array
    if (part.damage_images && part.damage_images.length > 0) {
      for (const img of part.damage_images) {
        if (img.Action) return img.Action;
        if (img.action) return img.action;
        if (img.suggested_action) return img.suggested_action;
        if (img.status) return img.status;
        if (img.repair_status) return img.repair_status;
      }
    }

    return "No Damage"; // Default to No Damage if no action found
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true); // Reset to first image when modal opens
      const initialAction = getInitialAction(selectedPart);
      setSelectedAction(initialAction);
    }
  }, [isOpen, selectedPart]);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      const nextView = initialActiveView || 0;
      activeViewRef.current = nextView;
      setActiveView(nextView);
    }
  }, [isOpen, initialFilter, initialSeverityFilter, initialActiveView]);

  useEffect(() => {
    if (!isOpen) {
      hasSeededInitialSelectionRef.current = false;
      hasModalSelectionRef.current = false;
      return;
    }

    if (
      partDetail &&
      !hasSeededInitialSelectionRef.current &&
      !hasModalSelectionRef.current
    ) {
      hasSeededInitialSelectionRef.current = true;
      syncSelectedPartState(partDetail, partDetail.svgId || clickedPart);
    }
    // Seed from the opener only once. After a modal stencil click, local
    // selection state is the source of truth and must not be overwritten.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, partDetail]);

  const handleActiveViewChange = (view) => {
    activeViewRef.current = view;
    setActiveView(view);
  };

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

const handlecommentPart = (part) => {
    setSelectedPart(part);
    setIsCommentModalOpen(true);
  };

const handleCommentModalClose = () => {
    setIsCommentModalOpen(false);
    setSelectedPart(null);
  };

  const openImageLightbox = (images, startIndex = 0, part) => {
    const mappedImages = images.map((image, index) => ({
      id: image.id || `${part?.name || "part"}-${index}`,
      imageUrl: getImageUrl(image),
      title: image.partName || part?.name || `Image ${index + 1}`,
      side: image.side || part?.side || part?.name || `Image ${index + 1}`,
      caseId: assessmentData?.info?.num_plate || assessmentData?.info?.ref_num,
    }));

    setLightboxImages(mappedImages);
    setLightboxCurrentIndex(startIndex);
    setLightboxOpen(true);
  };

  const closeImageLightbox = () => {
    setLightboxOpen(false);
    setLightboxImages([]);
    setLightboxCurrentIndex(0);
  };

  if (!isOpen) return null;

  const assessmentDetails = assessmentData?.assessment_details || [];

  const viewSideByIndex = {
    0: "left",
    1: "front",
    2: "rear",
    3: "right",
    4: "top",
  };

  const getSelectedSide = () => viewSideByIndex[activeViewRef.current] || "";

  const getImageUrl = (image) => {
    const path = image?.upload_picture || image?.picture || image?.imageUrl;
    if (!path) return "";
    if (/^https?:\/\//i.test(path)) return path;
    return buildMediaUrl(path, {
      baseUrl: clientConfig.mediaBaseUrl,
      pathPrefix: clientConfig.mediaPathPrefix,
    });
  };

  const normalizeSide = (side) => {
    if (!side) return "";

    const normalized = side.toString().toLowerCase();
    if (normalized.includes("front")) return "front";
    if (normalized.includes("rear") || normalized.includes("back")) {
      return "rear";
    }
    if (normalized.includes("left")) return "left";
    if (normalized.includes("right")) return "right";
    return normalized;
  };

  const selectedSide = viewSideByIndex[activeView] || "";

  const getPartSide = (part) => {
    const explicitSide = normalizeSide(part.side);
    if (explicitSide) return explicitSide;

    const partName =
      part.part_name?.toLowerCase() || part.name?.toLowerCase() || "";
    if (partName.includes("front")) return "front";
    if (partName.includes("rear") || partName.includes("back")) return "rear";
    if (partName.includes("left")) return "left";
    if (partName.includes("right")) return "right";
    return "";
  };

  const getPartImages = (part) => {
    const hasDamageImages = part.damage_images?.length > 0;
    return hasDamageImages ? part.damage_images : part.part_images || [];
  };

  const getImagesForPartSide = (part, side) => {
    const images = getPartImages(part);
    const partSide = getPartSide(part);

    return images.filter((image) => {
      const imageSide = normalizeSide(image.side);
      const sideForImage = imageSide || partSide;
      return side ? !sideForImage || sideForImage === side : true;
    });
  };

  const normalizePartKey = (value) =>
    (value || "")
      .toString()
      .toLowerCase()
      .replace(/[_\s]+/g, "-")
      .replace(/-+/g, "-")
      .trim();

  const stripSidePrefix = (value) =>
    normalizePartKey(value).replace(/^(front|rear|back|left|right)-/, "");

  const partKeysMatch = (partValue, selectedValue) => {
    const partKey = normalizePartKey(partValue);
    const selectedKey = normalizePartKey(selectedValue);
    if (!partKey || !selectedKey) return false;

    const partBaseKey = stripSidePrefix(partKey);
    const selectedBaseKey = stripSidePrefix(selectedKey);

    return (
      partKey === selectedKey ||
      partBaseKey === selectedBaseKey ||
      partKey.endsWith(`-${selectedBaseKey}`) ||
      selectedKey.endsWith(`-${partBaseKey}`)
    );
  };

  const partHasImages = (part) =>
    (part.damage_images || part.damageImages || []).length > 0 ||
    (part.part_images || part.partImages || []).length > 0;

  const findAssessmentPart = (detail, selectedPartId = clickedPart) => {
    if (!detail) return null;

    const selectedValues = [
      detail.name,
      detail.part_name,
      detail.svgId,
      selectedPartId,
    ].filter(Boolean);

    const matches = assessmentDetails.filter((part) =>
      selectedValues.some((value) => partKeysMatch(part.part_name, value))
    );

    if (matches.length <= 1) return matches[0] || null;

    const selectedSide = getSelectedSide();
    const sameSideMatch = matches.find(
      (part) => normalizeSide(part.side) === selectedSide && partHasImages(part)
    );
    if (sameSideMatch) return sameSideMatch;

    return matches.find(partHasImages) || matches[0];
  };

  const toPartWithImages = (part) => {
    if (!part) return null;

    return {
      ...part,
      damage_images: part.damage_images || part.damageImages,
      part_images: part.part_images || part.partImages,
    };
  };

  const getPartSelectionState = (detail, selectedPartId = detail?.svgId) => {
    if (!detail) {
      return { part: null, images: [] };
    }

    const matchedPart = findAssessmentPart(detail, selectedPartId);
    const selectedPartForImages = toPartWithImages(matchedPart || detail);
    const nextPartDetail = toPartWithImages({
      ...detail,
      ...selectedPartForImages,
      name: detail.name || selectedPartForImages.part_name,
      svgId: detail.svgId || selectedPartId,
      healthText: detail.healthText || selectedPartForImages.healthText,
      color: detail.color || selectedPartForImages.color,
    });
    const sideImages = getImagesForPartSide(
      selectedPartForImages,
      getSelectedSide()
    ).map((image) => ({
      ...image,
      partName: selectedPartForImages.part_name || selectedPartForImages.name,
      side: image.side || selectedPartForImages.side || getSelectedSide(),
    }));

    return { part: nextPartDetail, images: sideImages };
  };

  const syncSelectedPartState = (detail, selectedPartId = detail?.svgId) => {
    const { part, images } = getPartSelectionState(detail, selectedPartId);

    setCurrentPartDetail(part);
    setDisplayedImages(images);
    setShowImageEmptyState(images.length === 0);
  };

  const handleModalPartSelect = (detail) => {
    const selectedPartId = detail?.svgId;

    hasModalSelectionRef.current = true;
    syncSelectedPartState(detail, selectedPartId);
    setPartName?.(detail);
  };

  const getIntensity = (part) => {
    if (part.Intensity) return parseInt(part.Intensity);
    if (part.intensity) return parseInt(part.intensity);
    if (part.damage_intensity) return parseInt(part.damage_intensity);
    if (part.severity) return parseInt(part.severity);
    if (part.damage_severity) return parseInt(part.damage_severity);

    if (part.damage_images && part.damage_images.length > 0) {
      for (const img of part.damage_images) {
        if (img.Intensity) return parseInt(img.Intensity);
        if (img.intensity) return parseInt(img.intensity);
        if (img.damage_intensity) return parseInt(img.damage_intensity);
        if (img.severity) return parseInt(img.severity);
        if (img.damage_severity) return parseInt(img.damage_severity);
      }
    }

    return 0;
  };

  const filterParts = () => {
    const selectedName = currentPartDetail?.name || currentPartDetail?.part_name;
    const selectedSvgId = currentPartDetail?.svgId;

    const filtered = assessmentDetails?.filter((part) => {
      return (
        partKeysMatch(part.part_name, selectedName) ||
        partKeysMatch(part.part_name, selectedSvgId)
      );
    });

    if (filtered?.length > 0) {
      return filtered;
    }

    if (currentPartDetail) {
      return [
        {
          ...currentPartDetail,
          damage_images:
            currentPartDetail.damage_images || currentPartDetail.damageImages,
          part_images:
            currentPartDetail.part_images || currentPartDetail.partImages,
        },
      ];
    }

    return [];
  };

  const filteredParts = filterParts();

  const getSeverityColor = (intensityOrPart) => {
    if (typeof intensityOrPart === "object" && intensityOrPart?.color) {
      return intensityOrPart.color;
    }

    let intensity;
    if (typeof intensityOrPart === "object") {
      intensity = getIntensity(intensityOrPart);
    } else {
      intensity = parseInt(intensityOrPart) || 0;
    }

    if (intensity > 8) return "#df3822";
    if (intensity > 0) return "#f8bc05";
    return "green ";
  };

  const getSeverityText = () => {
    const part = currentPartDetail || filteredParts[0];
    if (!part) return "No Damage";

    const colorText = (part.color || "").toString().toLowerCase();
    const healthText = (part.healthText || "").toString().toLowerCase();
    if (
      colorText.includes("239") ||
      colorText.includes("#df3822") ||
      colorText.includes("#ef3a3a") ||
      colorText.includes("red") ||
      healthText.includes("severe")
    ) {
      return "Severe Damage";
    }
    if (
      colorText.includes("251") ||
      colorText.includes("yellow") ||
      colorText.includes("amber") ||
      healthText.includes("moderate")
    ) {
      return "Moderate Damage";
    }

    const intensity = getIntensity(part);
    if (intensity > 8) return "Severe Damage";
    if (intensity > 0) return "Moderate Damage";
    return "No Damage";
  };

  const getSeverityTextColor = () => {
    return getSeverityColor(currentPartDetail || filteredParts[0]);
  };

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center"
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-tl-[24px] rounded-tr-[24px] w-full max-w-full h-[90vh] lg:max-w-[65%] relative overflow-hidden flex flex-col transition-transform duration-300 ease-out ${
          isAnimating ? "translate-y-0" : "translate-y-full"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button - Top Left */}
        <button
          className="absolute top-4 left-4 z-10 cursor-pointer size-[44px] flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors"
          onClick={handleClose}
        >
          <X size={24} className="text-gray-600" />
        </button>

        {/* Car SVG */}
        <div className="flex justify-center items-center w-full pt-8 pb-6 bg-[#EAEEEF]">
          <div className="w-full max-w-[600px] flex justify-center">
            <CarSVG
              partsDamageMap={partsDamageMap}
              setPartName={handleModalPartSelect}
              isInModal={true}
              clickedPart={clickedPart}
              setClickedPart={setClickedPart}
              initialActiveView={initialActiveView}
              onActiveViewChange={handleActiveViewChange}
            />
          </div>
        </div>

        {/* Header with Part Name and Severity */}
        <div className="box-border flex items-center justify-between px-6 py-4 flex-shrink-0 ">
          <div className="flex items-center gap-2">
            <span
              className="font-['Noto_Sans:SemiBold',_sans-serif] font-semibold text-[20px] text-black tracking-[-0.2px]"
              style={{
                fontVariationSettings: "'CTGR' 0, 'wdth' 100",
              }}
            >
              {currentPartDetail?.name ||
                currentPartDetail?.part_name ||
                "Unknown Part"}
            </span>
            <span className="text-[20px] text-gray-400">•</span>
            <span
              className="text-[20px] font-medium"
              style={{ color: getSeverityTextColor() }}
            >
              {getSeverityText()}
            </span>
          </div>
          <div className="flex gap-5">
            <button
              className="cursor-pointer size-[24px] flex items-center justify-center hover:bg-gray-100 rounded transition-colors"
              onClick={() =>
                filteredParts.length > 0 && handlecommentPart(filteredParts[0])
              }
            >
              <MessageSquareMore size={24} className="text-gray-600" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 pb-6 rounded-t-xl">
          {displayedImages.length > 0 ? (
            <div className="mb-6">
              <div className="flex gap-3 overflow-x-auto pb-2">
                {displayedImages.map((image, imgIndex) => (
                  <div
                    key={`${image.side || selectedSide}-${image.id || imgIndex}-${getImageUrl(image)}`}
                    className="h-[200px] w-[280px] flex-shrink-0 rounded-lg overflow-hidden cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() =>
                      openImageLightbox(
                        displayedImages,
                        imgIndex,
                        filteredParts[0] || currentPartDetail
                      )
                    }
                  >
                    <img
                      src={getImageUrl(image)}
                      alt={`${image.partName || selectedSide || "Selected part"} ${
                        imgIndex + 1
                      }`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : showImageEmptyState ? (
            <div className="text-center py-8">
              <p className="text-[#949ead] text-lg">
                No images available for this part/side.
              </p>
            </div>
          ) : null}

          {filteredParts.length === 0 && (
            <div className="text-center py-8">
              <p className="text-[#949ead] text-lg">
                No parts found matching the current filter.
              </p>
            </div>
          )}
        </div>
      </div>

<CommentPartModal
        isOpen={isCommentModalOpen}
        onClose={handleCommentModalClose}
        partData={selectedPart}
        assessmentData={assessmentData}
        onRefreshAssessment={onRefreshAssessment}
        selectedAction={selectedAction}
      />

      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={closeImageLightbox}
        images={lightboxImages}
        currentIndex={lightboxCurrentIndex}
      />
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default SingleDamagedPartModal;
